import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import { getImageUrl } from '../utils/imageHelper';

const Container = styled(motion.div)`
  padding: 100px 5% 50px 5%;
  min-height: 100vh;
  background: var(--background-start);
`;

const HeaderSection = styled.div`
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 10px;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--secondary-color);
  margin-bottom: 20px;
`;

const WeekInfo = styled.div`
  color: var(--primary-color);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const DaysContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const DaySection = styled.div`
  margin-bottom: 30px;
`;

const DayHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--border-color);
`;

const DayTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const DayDate = styled.span`
  font-size: 0.9rem;
  color: var(--secondary-color);
  font-weight: 500;
`;

const EpisodesRow = styled.div`
  display: flex;
  gap: 15px;
  overflow-x: auto;
  overflow-y: visible;
  padding: 10px 0 20px 0;
  margin: -10px 0;
  scrollbar-width: thin;
  scrollbar-color: var(--gradient-pink) transparent;
  
  @media (max-width: 768px) {
    flex-direction: column;
    overflow-x: visible;
    gap: 12px;
    padding: 0;
    margin: 0;
  }
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--gradient-pink);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: var(--gradient-cyan);
  }
  
  @media (max-width: 768px) {
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const EpisodeCard = styled(Link)`
  flex-shrink: 0;
  width: 280px;
  height: 160px;
  border-radius: 12px;
  overflow: visible;
  position: relative;
  background: linear-gradient(135deg, #1a102a, #0b0614);
  border: 1px solid var(--border-color);
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  text-decoration: none;
  display: flex;
  opacity: ${props => props.$disabled ? 0.6 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
  
  @media (max-width: 768px) {
    width: 100%;
    height: 140px;
    flex-shrink: 1;
  }
  
  &:hover {
    transform: ${props => props.$disabled ? 'none' : 'translateY(-5px)'};
    border-color: ${props => props.$disabled ? 'var(--border-color)' : 'var(--gradient-pink)'};
    box-shadow: ${props => props.$disabled ? 'none' : '0 10px 30px rgba(255, 0, 127, 0.3)'};
    z-index: 10;
  }
  
  @media (max-width: 768px) {
    &:hover {
      transform: none;
    }
  }
`;

const EpisodeImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.$imageUrl || ''});
  background-size: cover;
  background-position: center;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, transparent 40%, rgba(10, 2, 28, 0.95) 100%);
    z-index: 1;
  }
`;

const EpisodeInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  color: white;
  z-index: 2;
`;

const EpisodeTitle = styled.h3`
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const EpisodeMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: var(--secondary-color);
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const EpisodeNumber = styled.span`
  color: var(--primary-color);
`;


const TypeTag = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  color: var(--primary-color);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 600;
  z-index: 4;
  border: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    font-size: 0.55rem;
    padding: 2px 6px;
  }
`;

const EmptyDay = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--secondary-color);
  font-size: 0.9rem;
  background: var(--glass-bg);
  border-radius: 12px;
  border: 1px dashed var(--border-color);
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 100px 20px;
  color: var(--secondary-color);
  font-size: 1.2rem;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 50px 20px;
  color: #e94057;
  font-size: 1.1rem;
`;

const CalendarPage = () => {
  const [episodes, setEpisodes] = useState({});
  const [weekInfo, setWeekInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCheckingWeek, setIsCheckingWeek] = useState(false);
  const isCheckingWeekRef = useRef(false);
  const loadingRef = useRef(false);
  const weekInfoRef = useRef(null);
  const lastCheckedMondayRef = useRef(null);
  const retryCountRef = useRef(0);
  
  // Ref'leri güncelle
  useEffect(() => {
    isCheckingWeekRef.current = isCheckingWeek;
  }, [isCheckingWeek]);
  
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);
  
  useEffect(() => {
    weekInfoRef.current = weekInfo;
  }, [weekInfo]);

  // Türkçe gün isimleri
  const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
  const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  // Haftalık bölümleri çek (her zaman bu hafta)
  const fetchCalendarEpisodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Cache'i bypass et - her zaman güncel veriyi al
      // Kullanıcının saat dilimini API'ye gönder
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timestamp = new Date().getTime();
      const response = await fetch(
        `${getApiUrl(API_ENDPOINTS.GET_CALENDAR_EPISODES)}?week_offset=0&timezone=${encodeURIComponent(timezone)}&_t=${timestamp}`,
        {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Bölümler çekilemedi');
      }
      
      const data = await response.json();
      
      if (data.success) {
        // API'den gelen hafta bilgisini kontrol et
        // Kullanıcının local saat dilimine göre bugünü hesapla
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const todayLocalStr = `${year}-${month}-${day}`; // Local tarih string'i (YYYY-MM-DD)
        
        // Local saat dilimine göre bugünün gününü hesapla
        const dayOfWeek = today.getDay(); // 0 (Pazar) - 6 (Cumartesi)
        
        // Local saat dilimine göre pazartesiyi hesapla
        let currentMonday = new Date(today);
        if (dayOfWeek === 0) {
          currentMonday.setDate(today.getDate() - 6);
        } else {
          currentMonday.setDate(today.getDate() - (dayOfWeek - 1));
        }
        currentMonday.setHours(0, 0, 0, 0);
        
        // Local tarih string'ini oluştur
        const mondayYear = currentMonday.getFullYear();
        const mondayMonth = String(currentMonday.getMonth() + 1).padStart(2, '0');
        const mondayDay = String(currentMonday.getDate()).padStart(2, '0');
        const currentMondayStr = `${mondayYear}-${mondayMonth}-${mondayDay}`;
        
        const apiMondayStr = data.week_info.monday; // Zaten YYYY-MM-DD formatında
        
        // Eğer API yanlış haftayı döndürdüyse, tekrar istek at (max 3 kez)
        if (currentMondayStr !== apiMondayStr) {
          if (retryCountRef.current < 3) {
            retryCountRef.current++;
            console.log(`API yanlış haftayı döndürdü, tekrar istek atılıyor... (${retryCountRef.current}/3)`);
            // Loading'i false yap ve tekrar istek at
            setLoading(false);
            setTimeout(() => {
              fetchCalendarEpisodes();
            }, 1000);
            return;
          } else {
            console.warn('API yanlış haftayı döndürdü, max retry sayısına ulaşıldı. Mevcut veriyi kullanıyoruz.');
            retryCountRef.current = 0;
            // Yine de veriyi göster (yanlış olsa bile)
            setEpisodes(data.episodes || {});
            setWeekInfo(data.week_info);
            setIsCheckingWeek(false);
            setLoading(false);
            return;
          }
        }
        
        // Doğru hafta geldi, retry sayacını sıfırla ve veriyi kaydet
        retryCountRef.current = 0;
        setEpisodes(data.episodes || {});
        setWeekInfo(data.week_info);
        setIsCheckingWeek(false); // Kontrol tamamlandı
      } else {
        throw new Error(data.error || 'Bilinmeyen hata');
      }
    } catch (err) {
      console.error('Takvim verisi çekilirken hata:', err);
      setError(err.message);
      setIsCheckingWeek(false); // Hata durumunda da kontrolü sıfırla
    } finally {
      setLoading(false);
    }
  }, []);

  // İlk yükleme
  useEffect(() => {
    fetchCalendarEpisodes();
  }, [fetchCalendarEpisodes]);

  // Her 5 dakikada bir hafta kontrolü yap (yeni haftaya geçildiğinde güncellenmesi için)
  useEffect(() => {
    if (!weekInfo) return;
    
    let mounted = true;
    
    // Hafta kontrolü yap
    const checkOnce = () => {
      if (!mounted) return;
      
      // Ref'lerden güncel değerleri al
      if (isCheckingWeekRef.current || loadingRef.current || !weekInfoRef.current) return;
      
      const currentWeekInfo = weekInfoRef.current;
      
      // Kullanıcının local saat dilimine göre bugünü hesapla
      const today = new Date();
      
      // Local saat dilimine göre bugünün gününü hesapla
      const dayOfWeek = today.getDay(); // 0 (Pazar) - 6 (Cumartesi)
      
      // Local saat dilimine göre pazartesiyi hesapla
      let currentMonday = new Date(today);
      if (dayOfWeek === 0) {
        currentMonday.setDate(today.getDate() - 6);
      } else {
        currentMonday.setDate(today.getDate() - (dayOfWeek - 1));
      }
      currentMonday.setHours(0, 0, 0, 0);
      
      // Local tarih string'ini oluştur
      const mondayYear = currentMonday.getFullYear();
      const mondayMonth = String(currentMonday.getMonth() + 1).padStart(2, '0');
      const mondayDay = String(currentMonday.getDate()).padStart(2, '0');
      const currentMondayStr = `${mondayYear}-${mondayMonth}-${mondayDay}`;
      
      const apiMondayStr = currentWeekInfo.monday; // Zaten YYYY-MM-DD formatında
      
      // Sadece hafta değiştiyse ve daha önce kontrol edilmemişse yenile
      if (currentMondayStr !== apiMondayStr) {
        if (lastCheckedMondayRef.current !== currentMondayStr) {
          console.log('Yeni haftaya geçildi, takvim güncelleniyor...', {
            currentMonday: currentMondayStr,
            apiMonday: apiMondayStr
          });
          lastCheckedMondayRef.current = currentMondayStr;
          retryCountRef.current = 0; // Retry sayacını sıfırla
          setIsCheckingWeek(true);
          fetchCalendarEpisodes();
        }
      }
    };
    
    // İlk kontrolü 2 saniye sonra yap (sonsuz döngüyü önlemek için)
    const initialTimeout = setTimeout(() => {
      if (mounted) checkOnce();
    }, 2000);
    
    // Sonra her 5 dakikada bir kontrol et
    const interval = setInterval(() => {
      if (mounted) checkOnce();
    }, 5 * 60 * 1000); // 5 dakika

    return () => {
      mounted = false;
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [weekInfo?.monday, fetchCalendarEpisodes]); // Sadece monday değiştiğinde interval'i yeniden oluştur

  // AdminEditPage'den bölüm güncellendiğinde takvimi yenile
  useEffect(() => {
    const handleEpisodeUpdate = () => {
      fetchCalendarEpisodes();
    };

    window.addEventListener('episodeUpdated', handleEpisodeUpdate);
    return () => window.removeEventListener('episodeUpdated', handleEpisodeUpdate);
  }, [fetchCalendarEpisodes]);

  // Tarih formatla
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const dayName = dayNames[date.getDay() === 0 ? 6 : date.getDay() - 1];
    return `${day} ${month} ${dayName}`;
  };

  // Bölüm yayınlandı mı kontrol et
  const isEpisodeAired = (airDate) => {
    if (!airDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const episodeDate = new Date(airDate);
    episodeDate.setHours(0, 0, 0, 0);
    return episodeDate <= today;
  };

  // Haftanın tüm günlerini oluştur
  const getWeekDays = () => {
    if (!weekInfo) return [];
    
    const days = [];
    const monday = new Date(weekInfo.monday);
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      const dayKey = day.toISOString().split('T')[0];
      days.push({
        key: dayKey,
        date: day,
        display: formatDate(dayKey)
      });
    }
    
    return days;
  };

  // Bölüm card verisi oluştur
  const getEpisodeCardData = (episode) => {
    const typeMap = { movie: 'Film', tv: 'Dizi', anime: 'Anime' };
    
    // Bölüm banner'ı (thumbnail_path) varsa onu kullan, yoksa backdrop veya poster
    const imageUrlCandidate = episode.thumbnail_path || episode.backdrop_path || episode.poster_path;
    const imageUrl = imageUrlCandidate?.includes('/proxy-image.php')
      ? imageUrlCandidate
      : getImageUrl(imageUrlCandidate, 'w500');
    
    // Bölüm yayınlandı mı kontrol et
    const aired = isEpisodeAired(episode.air_date);
    // TMDB'den gelen bölümler için content_id yok, bu yüzden tıklanamaz
    // Ama eğer content_id varsa ve yayınlanmışsa tıklanabilir
    const hasContentId = episode.content_id && episode.content_id !== null && episode.content_id !== '';
    
    return {
      id: episode.tmdb_id,
      contentId: episode.content_id,
      title: episode.content_title,
      mediaType: episode.media_type,
      imageUrl: imageUrl,
      season: episode.season_number,
      episode: episode.episode_number,
      episodeTitle: episode.episode_title || `${episode.season_number}.Sezon ${episode.episode_number}.Bölüm`,
      airDate: episode.air_date,
      aired: aired && hasContentId, // Yayınlanmış ve sitede yüklü (content_id varsa) bölümler tıklanabilir
      mediaTypeLabel: typeMap[episode.media_type] || 'İçerik'
    };
  };


  if (loading) {
    return (
      <Container initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Header />
        <LoadingContainer>Yükleniyor...</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Header />
        <ErrorContainer>
          <p>Hata: {error}</p>
          <button 
            onClick={() => fetchCalendarEpisodes()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: 'var(--gradient-pink)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Tekrar Dene
          </button>
        </ErrorContainer>
      </Container>
    );
  }

  const weekDays = getWeekDays();

  return (
    <Container
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Header />
      
      <HeaderSection>
        <Title>Takvim</Title>
        <Subtitle>Yakında gelecek bölümleri takip edin</Subtitle>
        
        <WeekInfo>
          {weekInfo && (
            <>
              {formatDate(weekInfo.monday)} - {formatDate(weekInfo.sunday)}
            </>
          )}
        </WeekInfo>
      </HeaderSection>

      <DaysContainer>
        {weekDays.map((day) => {
          const dayEpisodes = episodes[day.key] || [];
          
          return (
            <DaySection key={day.key}>
              <DayHeader>
                <DayTitle>{day.display}</DayTitle>
                {dayEpisodes.length > 0 && (
                  <DayDate>{dayEpisodes.length} bölüm</DayDate>
                )}
              </DayHeader>
              
              {dayEpisodes.length > 0 ? (
                <EpisodesRow>
                  {dayEpisodes.map((episode, index) => {
                    const cardData = getEpisodeCardData(episode);
                    // Sadece yayınlanmış ve sitede yüklü bölümler için link oluştur
                    const watchUrl = cardData.aired
                      ? `/watch/${cardData.mediaType === 'anime' ? 'tv' : cardData.mediaType}/${cardData.id}/season/${cardData.season}/episode/${cardData.episode}`
                      : '#';
                    
                    return (
                      <EpisodeCard 
                        key={`${episode.tmdb_id || episode.id}-${episode.season_number}-${episode.episode_number}-${index}`} 
                        to={watchUrl}
                        $disabled={!cardData.aired}
                        onClick={(e) => {
                          if (!cardData.aired) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <EpisodeImage $imageUrl={cardData.imageUrl} />
                        <TypeTag>{cardData.mediaTypeLabel}</TypeTag>
                        <EpisodeInfo>
                          <EpisodeTitle>{cardData.title}</EpisodeTitle>
                          <EpisodeMeta>
                            <EpisodeNumber>
                              {cardData.season}.Sezon {cardData.episode}.Bölüm
                            </EpisodeNumber>
                          </EpisodeMeta>
                        </EpisodeInfo>
                      </EpisodeCard>
                    );
                  })}
                </EpisodesRow>
              ) : (
                <EmptyDay>Bu gün için bölüm bulunmuyor</EmptyDay>
              )}
            </DaySection>
          );
        })}
      </DaysContainer>
    </Container>
  );
};

export default CalendarPage;
